(() => {
  'use strict';

  const configNode = document.getElementById('OttAnalyticsP17Config');
  if (!configNode) return;

  let config = {};
  try {
    config = JSON.parse(configNode.textContent || '{}');
  } catch (_error) {
    return;
  }

  const DEBUG = Boolean(config.debug);
  const PREFIX = 'ottrecord:';
  const recent = new Map();

  const debug = (...args) => {
    if (DEBUG && window.console) console.info('[OTTRecord P17]', ...args);
  };

  const cleanText = (value, limit = 120) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, limit);

  const integer = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const canTrack = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return !privacy || typeof privacy.userCanBeTracked !== 'function' || privacy.userCanBeTracked();
    } catch (_error) {
      return false;
    }
  };

  const basePayload = () => ({
    phase: '390R-P17',
    page_type: cleanText(config.pageType, 40),
    template: cleanText(config.template, 60),
    path: cleanText(window.location.pathname, 180),
    currency: cleanText(config.currency, 8),
    customer_state: config.customerState === 'authenticated' ? 'authenticated' : 'guest'
  });

  const sanitize = (payload = {}) => {
    const result = {};
    Object.entries({ ...basePayload(), ...payload }).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'string') result[key] = cleanText(value, 180);
      else if (typeof value === 'number' || typeof value === 'boolean') result[key] = value;
    });
    return result;
  };

  const publish = (name, payload = {}, dedupeKey = '') => {
    const eventName = `${PREFIX}${name}`;
    const key = dedupeKey || `${eventName}:${payload.product_id || ''}:${payload.variant_id || ''}`;
    const now = Date.now();
    if (recent.has(key) && now - recent.get(key) < 450) return;
    recent.set(key, now);
    window.setTimeout(() => recent.delete(key), 1200);

    if (!canTrack()) {
      debug('blocked by consent', eventName);
      return;
    }

    const data = sanitize(payload);
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(eventName, data);
      }
    } catch (error) {
      debug('Shopify publish failed', eventName, error);
    }

    try {
      window.dispatchEvent(new CustomEvent('ottrecord:analytics', {
        detail: { event: eventName, data }
      }));
    } catch (_error) {}

    debug(eventName, data);
  };

  const closestBeat = (element) => element?.closest?.('[data-beat-card], [data-ott-product-beat]');
  const beatPayload = (element) => {
    const card = closestBeat(element);
    const productRoot = element?.closest?.('[data-ott-product-beat]');
    const product = config.product || {};
    return {
      product_id: cleanText(card?.dataset?.productId || productRoot?.dataset?.productId || product.id, 40),
      product_handle: cleanText(product.handle, 100),
      product_title: cleanText(card?.dataset?.title || element?.dataset?.previewTitle || product.title, 120),
      product_url: cleanText(card?.dataset?.productUrl || card?.dataset?.cardUrl || product.handle && `/products/${product.handle}`, 180),
      price: integer(card?.dataset?.price || product.price),
      bpm: integer(card?.dataset?.bpm),
      season: cleanText(card?.dataset?.season, 60),
      placement: card?.classList?.contains('is-featured') ? 'featured' : card ? 'catalogue' : 'product'
    };
  };

  const selectedVariant = (form) => {
    const checked = form?.querySelector('input[name="id"]:checked');
    const hidden = form?.querySelector('input[name="id"][type="hidden"]');
    return checked || hidden || form?.querySelector('[name="id"]');
  };

  const serviceType = (form) => {
    const root = form?.closest?.('[data-service-type]');
    return cleanText(root?.dataset?.serviceType || form?.querySelector('input[name="properties[Type de prestation]"]')?.value || 'service', 80);
  };

  const PENDING_CART_KEY = 'ottrecord-p17-pending-cart-add';
  const savePendingCartAdd = (payload) => {
    try {
      sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify({ ...payload, ts: Date.now() }));
    } catch (_error) {}
  };
  const readPendingCartAdd = () => {
    try { return JSON.parse(sessionStorage.getItem(PENDING_CART_KEY) || 'null'); } catch (_error) { return null; }
  };
  const clearPendingCartAdd = () => {
    try { sessionStorage.removeItem(PENDING_CART_KEY); } catch (_error) {}
  };
  const resolvePendingCartAdd = () => {
    const pending = readPendingCartAdd();
    if (!pending || !pending.variant_id) return;
    if (Date.now() - Number(pending.ts || 0) > 10 * 60 * 1000) { clearPendingCartAdd(); return; }
    if (config.pageType !== 'cart') return;
    const ids = Array.isArray(config.cart?.variantIds) ? config.cart.variantIds.map(String) : [];
    const found = ids.includes(String(pending.variant_id));
    publish(found ? 'add_to_cart_success' : 'add_to_cart_error', {
      product_id: cleanText(pending.product_id, 40),
      variant_id: cleanText(pending.variant_id, 40),
      item_type: cleanText(pending.item_type, 30),
      source: 'cart-confirmation',
      reason: found ? '' : 'variant_not_found_in_cart'
    }, `cart-resolve:${pending.variant_id}:${found}`);
    clearPendingCartAdd();
  };

  document.addEventListener('click', (event) => {
    const target = event.target.closest('button, a, input[type="submit"]');
    if (!target) return;

    if (target.matches('[data-ott-context-action]')) {
      publish('sticky_cta_clicked', {
        action: cleanText(target.dataset.action, 40),
        product_id: cleanText(target.dataset.productId, 40),
        label: cleanText(target.textContent, 80)
      });
    }

    if (target.matches('[data-ott-proof-play]')) {
      publish('before_after_play', { side: cleanText(target.dataset.ottProofPlay, 20) });
    }

    if (target.matches('[data-ott-play], [data-preview-url], [data-player-play]')) {
      publish('beat_play', beatPayload(target), `beat-play:${beatPayload(target).product_id}:${target.dataset.previewTitle || ''}`);
      return;
    }

    if (target.matches('[data-favorite-id]')) {
      const activeBefore = target.getAttribute('aria-pressed') === 'true';
      publish(activeBefore ? 'favorite_removed' : 'favorite_added', {
        ...beatPayload(target),
        product_id: cleanText(target.dataset.favoriteId, 40)
      });
      return;
    }

    if (target.matches('[data-open-license], [data-open-featured-license]')) {
      publish('license_options_opened', beatPayload(target));
      return;
    }

    if (target.matches('[data-license-option]')) {
      publish('license_selected', {
        ...beatPayload(target),
        variant_id: cleanText(target.dataset.variantId, 40),
        variant_title: cleanText(target.dataset.variantTitle, 120),
        variant_price_label: cleanText(target.dataset.variantPrice, 40)
      });
      return;
    }

    if (target.matches('[data-view]')) {
      publish('catalogue_view_changed', { view: cleanText(target.dataset.view, 20) });
      return;
    }

    if (target.matches('[data-favorites-toggle]')) {
      publish('favorites_filter_used', {
        enabled: target.getAttribute('aria-pressed') !== 'true'
      });
      return;
    }

    if (target.matches('[data-discovery-filter]')) {
      publish('discovery_filter_used', {
        discovery_mode: cleanText(target.dataset.discoveryFilter, 40),
        label: cleanText(target.textContent, 80)
      });
      return;
    }

    if (target.matches('[data-clear-recents]')) {
      publish('recent_beats_cleared', { source: 'beatstore' });
      return;
    }

    if (target.closest('[data-related-beat-link]')) {
      publish('related_beat_opened', beatPayload(target));
      return;
    }

    if (target.closest('[data-recent-beat-link]')) {
      publish('recent_beat_opened', beatPayload(target));
      return;
    }

    const paginationLink = target.closest('[data-pagination] a, [data-beat-pagination] a');
    if (paginationLink) {
      const url = new URL(paginationLink.href, window.location.origin);
      publish('catalogue_page_changed', {
        page: integer(url.searchParams.get('page') || url.searchParams.get('beat_page')) || 1
      });
      return;
    }

    const checkout = target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
    if (checkout) {
      publish('checkout_started_intent', {
        item_count: integer(config.cart?.itemCount) || document.querySelectorAll('[data-cart-item], .cart-item').length,
        cart_total: integer(config.cart?.totalPrice)
      }, 'checkout-started-intent');
      return;
    }

    const href = target.closest('a[href]')?.href || '';
    if (href) {
      let url;
      try { url = new URL(href, window.location.origin); } catch (_error) { url = null; }
      if (url && /(^|\.)account\.ottrecord\.com$/i.test(url.hostname)) {
        const path = url.pathname.toLowerCase();
        let module = 'login';
        if (path.includes('commande')) module = 'orders';
        else if (path.includes('projet')) module = 'projects';
        else if (path.includes('fichier')) module = 'files';
        else if (path.includes('licence')) module = 'licenses';
        else if (path.includes('support')) module = 'support';
        publish('ottcity_click', { module, destination_path: cleanText(url.pathname, 140) });
        return;
      }

      if (url && (url.pathname.includes('soumettre') || target.closest('[data-heartz-submit]'))) {
        publish('heartz_action', { action: 'submit_click', destination_path: cleanText(url.pathname, 140) });
        return;
      }

      if (target.closest('[data-community-card], [data-proof-card]')) {
        publish('community_proof_click', {
          destination_path: cleanText(url.pathname, 140),
          label: cleanText(target.textContent, 100)
        });
      }
    }
  }, true);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-variant-radio]')) {
      publish('license_selected', {
        ...beatPayload(target),
        variant_id: cleanText(target.value, 40),
        variant_title: cleanText(target.dataset.title, 120),
        variant_price_label: cleanText(target.dataset.price, 40),
        stems_included: target.dataset.stems === 'true'
      });
      return;
    }

    if (target.matches('[data-p09-variant], [data-p09b-variant], [data-p09c-variant]')) {
      publish('service_variant_selected', {
        service_type: serviceType(target.form),
        product_id: cleanText(config.product?.id, 40),
        variant_id: cleanText(target.value, 40),
        variant_title: cleanText(target.dataset.title, 120),
        variant_price_label: cleanText(target.dataset.price, 40)
      });
      return;
    }

    if (target.closest('[data-catalog-root]') && target.matches('select, input[type="checkbox"], input[type="radio"]')) {
      publish('filter_used', {
        filter_name: cleanText(target.name || target.dataset.filter || target.id, 80),
        filter_value: cleanText(target.value, 100),
        checked: typeof target.checked === 'boolean' ? target.checked : undefined
      }, `filter:${target.name || target.id}:${target.value}:${target.checked}`);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-global-search-form], form[role="search"]')) {
      const input = form.querySelector('input[type="search"], input[name="q"]');
      const query = cleanText(input?.value, 100);
      publish('search_used', {
        search_scope: form.closest('[data-ott-beatstore]') ? 'beatstore' : 'site',
        query_length: query.length,
        ...(config.trackSearchTerms ? { search_term: query } : {})
      });
    }

    if (form.closest('[data-ott-product-beat]')) {
      const variant = selectedVariant(form);
      const pendingPayload = {
        ...beatPayload(form),
        variant_id: cleanText(variant?.value, 40),
        variant_title: cleanText(variant?.dataset?.title || form.querySelector('[data-variant-title]')?.textContent, 120),
        item_type: 'beat'
      };
      savePendingCartAdd(pendingPayload);
      publish('add_to_cart_intent', pendingPayload, `add-beat:${variant?.value || ''}`);
      return;
    }

    if (form.closest('[data-ott-product-service], [data-ott-product-service-audio], [data-ott-product-service-creative], [data-ott-product-service-guidance]')) {
      const variant = selectedVariant(form);
      const servicePayload = {
        product_id: cleanText(config.product?.id, 40),
        product_handle: cleanText(config.product?.handle, 100),
        product_title: cleanText(config.product?.title, 120),
        service_type: serviceType(form),
        variant_id: cleanText(variant?.value, 40),
        variant_title: cleanText(variant?.dataset?.title, 120),
        price: integer(config.product?.price),
        item_type: 'service'
      };
      savePendingCartAdd(servicePayload);
      publish('service_form_submit', servicePayload, `service-submit:${config.product?.id || ''}:${variant?.value || ''}`);
      if (form.closest('[data-ott-product-service="389r"]')?.querySelector('.ott389-service__after-order')) {
        publish('service_direct_add', servicePayload, `service-direct:${config.product?.id || ''}:${variant?.value || ''}`);
      }
    }
  }, true);

  window.addEventListener('ottrecord:cart:add-success', (event) => {
    const detail = event.detail || {};
    publish('add_to_cart_success', {
      product_id: cleanText(detail.product_id, 40),
      variant_id: cleanText(detail.variant_id, 40),
      item_type: cleanText(detail.item_type || 'beat', 30),
      source: cleanText(detail.source || 'ajax', 40)
    }, `cart-success:${detail.variant_id || ''}`);
    clearPendingCartAdd();
  });

  window.addEventListener('ottrecord:cart:add-error', (event) => {
    const detail = event.detail || {};
    publish('add_to_cart_error', {
      product_id: cleanText(detail.product_id, 40),
      variant_id: cleanText(detail.variant_id, 40),
      item_type: cleanText(detail.item_type || 'beat', 30),
      source: cleanText(detail.source || 'ajax', 40),
      reason: cleanText(detail.reason || 'request_failed', 80)
    }, `cart-error:${detail.variant_id || ''}`);
  });

  resolvePendingCartAdd();

  document.addEventListener('ended', (event) => {
    if (!(event.target instanceof HTMLAudioElement)) return;
    const root = event.target.closest('[data-ott-product-beat], [data-player]');
    if (!root) return;
    publish('beat_complete', beatPayload(event.target), `beat-complete:${beatPayload(event.target).product_id}`);
  }, true);

  window.OTTRecordAnalytics = Object.freeze({
    publish: (name, payload) => publish(cleanText(name, 80).replace(/^ottrecord:/, ''), payload),
    version: 'V501-P17'
  });

  debug('ready', config);
})();
